import { forwardRef, useMemo, useState } from "react";
import { History, Trash2, Zap } from "lucide-react";
import { suggestCodes } from "@/lib/reportTemplate";

const inputCls =
  "w-full rounded-md bg-[#0b1928] border border-[#243a52] focus:border-[#0ea5e9]/70 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-slate-600 transition-colors";

const EMPTY_SUGG = { items: [], start: -1, end: -1 };

/**
 * PicInput — input/textarea with two dropdown sources:
 *   • shortcut-code suggestions (when `codes` given and caret token matches a code prefix)
 *   • history of previous entries (`history`)
 */
const PicInput = forwardRef(function PicInput(
  {
    role,
    label,
    value,
    history = [],
    onChange,
    onCommit,
    codes,
    onPickCode,
    multiline = false,
    rows = 3,
    placeholder,
    headerRight,
    className = "",
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const [codeSugg, setCodeSugg] = useState(EMPTY_SUGG);

  const histItems = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    const list = q ? history.filter((n) => n.toLowerCase().includes(q) && n !== value) : history;
    return list.slice(0, 8);
  }, [value, history]);

  const showCodes = codeSugg.items.length > 0;
  const active = showCodes ? codeSugg.items : histItems;

  const close = () => {
    setOpen(false);
    setHi(-1);
    setCodeSugg(EMPTY_SUGG);
  };

  const pickHistory = (name) => {
    onChange(name);
    onCommit?.(name);
    close();
  };
  const pickCode = (c) => {
    onPickCode?.(c, codeSugg.start, codeSugg.end);
    close();
  };
  const pick = (i) => (showCodes ? pickCode(active[i]) : pickHistory(active[i]));

  const onKeyDown = (e) => {
    if (!open || active.length === 0) {
      if (e.key === "Enter" && !multiline) onCommit?.(value);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => (h + 1) % active.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => (h <= 0 ? active.length - 1 : h - 1));
    } else if ((e.key === "Enter" || e.key === "Tab") && showCodes) {
      e.preventDefault();
      pick(hi >= 0 ? hi : 0);
    } else if (e.key === "Enter" && (hi >= 0 || !multiline)) {
      e.preventDefault();
      if (hi >= 0) pick(hi);
      else {
        onCommit?.(value);
        close();
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  const common = {
    ref,
    "data-testid": `pic-input-${role}`,
    value: value || "",
    autoComplete: "off",
    onChange: (e) => {
      const raw = e.target.value;
      onChange(raw);
      setOpen(true);
      setHi(-1);
      setCodeSugg(codes ? suggestCodes(raw, e.target.selectionStart, codes) || EMPTY_SUGG : EMPTY_SUGG);
    },
    onFocus: () => setOpen(true),
    onBlur: () => {
      setTimeout(close, 120);
      onCommit?.(value);
    },
    onKeyDown,
    placeholder: placeholder ?? `Nama ${label}`,
  };

  return (
    <div className={`relative ${className}`} data-testid={`pic-field-${role}`}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</label>
        {headerRight}
      </div>
      <div className="relative">
        {multiline ? (
          <textarea {...common} rows={rows} className={`${inputCls} resize-y leading-relaxed pr-9`} />
        ) : (
          <input {...common} className={`${inputCls} pr-9`} />
        )}
        {(value || "").length > 0 && (
          <button
            type="button"
            data-testid={`pic-clear-${role}`}
            title={`Hapus isi ${label}`}
            aria-label={`Hapus isi ${label}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange("");
              onCommit?.("");
              close();
            }}
            className={`absolute right-2 ${multiline ? "top-2.5" : "top-1/2 -translate-y-1/2"} p-1 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && active.length > 0 && (
        <ul
          data-testid={showCodes ? `code-suggestions-${role}` : `pic-suggestions-${role}`}
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-[#243a52] bg-[#0d243a] shadow-xl shadow-black/40"
        >
          {showCodes
            ? active.map((c, i) => (
                <li key={c.id}>
                  <button
                    type="button"
                    data-testid={`code-suggestion-${c.code}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickCode(c)}
                    className={`w-full text-left px-3 py-2 transition-colors ${
                      i === hi || (hi < 0 && i === 0) ? "bg-[#0ea5e9]/20" : "hover:bg-[#122a42]"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-mono text-xs text-[#7dd3fc] font-semibold">
                      <Zap className="h-3 w-3 text-[#fbbf24]" />
                      {c.code}
                    </div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {(c.kejadian || c.text || "").replace(/\n/g, " · ")}
                    </div>
                  </button>
                </li>
              ))
            : active.map((n, i) => (
                <li key={n}>
                  <button
                    type="button"
                    data-testid={`pic-suggestion-${role}-${i}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickHistory(n)}
                    className={`w-full flex items-start gap-2 text-left px-3 py-2 text-sm transition-colors ${
                      i === hi ? "bg-[#0ea5e9]/20 text-white" : "text-slate-200 hover:bg-[#122a42]"
                    }`}
                  >
                    <History className="h-3 w-3 mt-1 shrink-0 text-slate-500" />
                    <span className={multiline ? "line-clamp-2 whitespace-pre-line" : "truncate"}>{n}</span>
                  </button>
                </li>
              ))}
        </ul>
      )}
    </div>
  );
});

export default PicInput;
